import { useCallback, useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties, HTMLAttributes, RefObject } from "react";

interface Position {
  left: number;
  top: number;
}

interface InspectorPositionOptions {
  open: boolean;
  triggerRef: RefObject<HTMLElement | null>;
  panelRef: RefObject<HTMLElement | null>;
  /** Recheck viewport placement when the portal moves, such as entering fullscreen. */
  container?: Element | null;
}

type DragHandleProps = Pick<
  HTMLAttributes<HTMLElement>,
  "onPointerDown" | "onPointerMove" | "onPointerUp" | "onPointerCancel" | "onLostPointerCapture" | "onKeyDown"
>;

interface Drag {
  pointerId: number;
  element: HTMLElement;
  clientX: number;
  clientY: number;
  position: Position;
}

const MARGIN = 12;
const TRIGGER_GAP = 8;

function viewport() {
  const visual = window.visualViewport;
  return {
    left: visual?.offsetLeft ?? 0,
    top: visual?.offsetTop ?? 0,
    width: visual?.width ?? window.innerWidth,
    height: visual?.height ?? window.innerHeight,
  };
}

/** Position a portalled inspector in the viewport, independently of player clipping and scroll. */
export function useInspectorPosition({ open, triggerRef, panelRef, container }: InspectorPositionOptions): {
  style: CSSProperties;
  isDragging: boolean;
  dragHandleProps: DragHandleProps;
  resetPosition: () => void;
} {
  const [position, setPosition] = useState<Position | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const positionRef = useRef<Position | null>(null);
  const drag = useRef<Drag | null>(null);

  const clamp = useCallback(
    (next: Position): Position => {
      const bounds = viewport();
      const panel = panelRef.current?.getBoundingClientRect();
      const minimumLeft = bounds.left + MARGIN;
      const minimumTop = bounds.top + MARGIN;
      return {
        left: Math.min(
          Math.max(minimumLeft, next.left),
          Math.max(minimumLeft, bounds.left + bounds.width - MARGIN - (panel?.width ?? 0)),
        ),
        top: Math.min(
          Math.max(minimumTop, next.top),
          Math.max(minimumTop, bounds.top + bounds.height - MARGIN - (panel?.height ?? 0)),
        ),
      };
    },
    [panelRef],
  );

  const place = useCallback(
    (next: Position) => {
      const clamped = clamp(next);
      const previous = positionRef.current;
      if (previous?.left === clamped.left && previous.top === clamped.top) return;
      positionRef.current = clamped;
      setPosition(clamped);
    },
    [clamp],
  );

  const defaultPosition = useCallback((): Position => {
    const bounds = viewport();
    const trigger = triggerRef.current?.getBoundingClientRect();
    const panel = panelRef.current?.getBoundingClientRect();
    if (!trigger || !panel) return { left: bounds.left + MARGIN, top: bounds.top + MARGIN };
    const below = trigger.bottom + TRIGGER_GAP;
    const above = trigger.top - TRIGGER_GAP - panel.height;
    return {
      left: trigger.right - panel.width,
      top: below + panel.height > bounds.top + bounds.height - MARGIN && above >= bounds.top + MARGIN ? above : below,
    };
  }, [panelRef, triggerRef]);

  const stopDragging = useCallback(() => {
    const active = drag.current;
    if (!active) return;
    drag.current = null;
    setIsDragging(false);
    if (active.element.hasPointerCapture?.(active.pointerId)) {
      active.element.releasePointerCapture(active.pointerId);
    }
  }, []);

  const resetPosition = useCallback(() => {
    stopDragging();
    place(defaultPosition());
  }, [defaultPosition, place, stopDragging]);

  useLayoutEffect(() => {
    if (!open) {
      stopDragging();
      return;
    }
    const keepVisible = () => place(positionRef.current ?? defaultPosition());
    keepVisible();
    window.addEventListener("resize", keepVisible);
    window.addEventListener("blur", stopDragging);
    const visual = window.visualViewport;
    visual?.addEventListener("resize", keepVisible);
    visual?.addEventListener("scroll", keepVisible);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(keepVisible);
    if (panelRef.current) observer?.observe(panelRef.current);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", keepVisible);
      window.removeEventListener("blur", stopDragging);
      visual?.removeEventListener("resize", keepVisible);
      visual?.removeEventListener("scroll", keepVisible);
      stopDragging();
    };
  }, [container, defaultPosition, open, panelRef, place, stopDragging]);

  const dragHandleProps: DragHandleProps = {
    onPointerDown: (event) => {
      if (!open || event.button !== 0 || event.isPrimary === false || drag.current) return;
      const target = event.target;
      const control =
        target instanceof Element ? target.closest("button, a, input, select, textarea, [role=button]") : null;
      if (control && control !== event.currentTarget) return;
      const start = positionRef.current ?? clamp(defaultPosition());
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // Do not enter a drag that cannot receive its release/cancel event.
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.focus({ preventScroll: true });
      drag.current = {
        pointerId: event.pointerId,
        element: event.currentTarget,
        clientX: event.clientX,
        clientY: event.clientY,
        position: start,
      };
      setIsDragging(true);
    },
    onPointerMove: (event) => {
      const active = drag.current;
      if (!active || active.pointerId !== event.pointerId) return;
      event.preventDefault();
      event.stopPropagation();
      place({
        left: active.position.left + event.clientX - active.clientX,
        top: active.position.top + event.clientY - active.clientY,
      });
    },
    onPointerUp: (event) => {
      if (drag.current?.pointerId === event.pointerId) stopDragging();
    },
    onPointerCancel: (event) => {
      if (drag.current?.pointerId === event.pointerId) stopDragging();
    },
    onLostPointerCapture: (event) => {
      if (drag.current?.pointerId === event.pointerId) stopDragging();
    },
    onKeyDown: (event) => {
      if (!open || event.target !== event.currentTarget || event.altKey || event.ctrlKey || event.metaKey) return;
      if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home"].includes(event.key)) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.key === "Home") return resetPosition();
      stopDragging();
      const step = event.shiftKey ? 48 : 16;
      const current = positionRef.current ?? defaultPosition();
      place({
        left: current.left + (event.key === "ArrowRight" ? step : event.key === "ArrowLeft" ? -step : 0),
        top: current.top + (event.key === "ArrowDown" ? step : event.key === "ArrowUp" ? -step : 0),
      });
    },
  };

  return {
    style: { position: "fixed", left: position?.left ?? MARGIN, top: position?.top ?? MARGIN },
    isDragging,
    dragHandleProps,
    resetPosition,
  };
}
